import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

export default function Pricing() {
  const [period, setPeriod] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api("/plans").then(r => setPlans(r.plans)).catch(() => {});
  }, []);

  const features = {
    discovery: [
      "10 factures / mois",
      "Devis, factures, notes de crédit de base",
      "Clients & produits illimités",
      "Export PDF",
      { text: "Relances automatiques", off: true },
      { text: "Multi-entreprises", off: true },
      { text: "Équipe multi-utilisateurs", off: true },
    ],
    solo: [
      "Factures illimitées",
      "Devis, BC, factures, notes de crédit",
      "Relances auto (3 niveaux)",
      "Multi-entreprises",
      "Sync cloud (Drive, Dropbox, iCloud)",
      "Signature électronique",
      "Export Excel",
      { text: "Paiement en ligne", off: true },
      { text: "Équipe", off: true },
    ],
    pro: [
      "Tout Solo +",
      "Jusqu'à 3 utilisateurs",
      "Paiement en ligne (Stripe)",
      "Portail client sécurisé",
      "Rapprochement bancaire",
      "Templates sectoriels",
      "Multi-devises (XPF/EUR/USD)",
      { text: "Facturation récurrente", off: true },
      { text: "Export FEC", off: true },
    ],
    business: [
      "Tout Pro +",
      "Jusqu'à 10 utilisateurs",
      "Facturation récurrente (abos)",
      "API publique + Webhooks",
      "Export FEC (conformité)",
      "Support prioritaire",
      "HTML standalone offline offert",
      "Audit log avancé",
    ],
  };

  const planOrder = ["discovery", "solo", "pro", "business"];
  const orderedPlans = planOrder.map(id => plans.find(p => p.id === id)).filter(Boolean);

  const getPrice = (plan) => period === "annual" ? plan.price_xpf_annual : plan.price_xpf_monthly;
  const getEur = (plan) => period === "annual" ? plan.price_eur_annual : plan.price_eur_monthly;
  const perLabel = period === "annual" ? "/an" : "/mois";

  return (
    <>
      <PublicNav />
      <section className="section" style={{textAlign:'center', paddingBottom:'30px'}}>
        <div style={{display:'inline-flex', alignItems:'center', gap:6, padding:'5px 14px', background:'var(--primary-soft)', color:'var(--primary)', borderRadius:'var(--radius-pill)', fontSize:12.5, fontWeight:600, letterSpacing:'0.04em', marginBottom:20}}>💳 Tarification transparente · en francs pacifiques</div>
        <h2>Choisissez le plan adapté à votre activité</h2>
        <p className="lead" style={{margin:'12px auto 8px'}}>Démarrez immédiatement avec 14 jours d'essai Pro — inscription sans carte bancaire.</p>
        <p style={{margin:'0 auto 30px', color:'var(--ink-muted)', fontSize:14, maxWidth:600}}>
          Pendant l'essai, toutes les fonctionnalités Pro sont débloquées. À tout moment, vous pouvez choisir un plan payant pour les conserver après les 14 jours.
        </p>
        <div className="period-toggle" data-testid="period-toggle">
          <button className={period==="monthly"?"active":""} onClick={()=>setPeriod("monthly")} data-testid="period-monthly">Mensuel</button>
          <button className={period==="annual"?"active":""} onClick={()=>setPeriod("annual")} data-testid="period-annual">Annuel <span className="save">−20%</span></button>
        </div>
      </section>

      <section className="section" style={{paddingTop:0, paddingBottom:60}} data-testid="pricing-timeline">
        <div style={{maxWidth:920, margin:'0 auto', padding:'32px', background:'var(--surface, #fff)', borderRadius:'var(--radius-lg, 16px)', border:'1px solid var(--border)'}}>
          <h3 style={{textAlign:'center', fontFamily:'var(--font-display, serif)', fontSize:22, fontWeight:500, marginTop:0, marginBottom:8}}>Comment ça marche pour vous</h3>
          <p style={{textAlign:'center', color:'var(--ink-muted)', fontSize:13, margin:'0 auto 32px', maxWidth:540}}>
            Aucune mauvaise surprise. Voici exactement ce qui se passe étape par étape :
          </p>
          <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:16, position:'relative'}}>
            <div style={{position:'absolute', top:22, left:'12.5%', right:'12.5%', height:2, background:'linear-gradient(90deg, var(--primary) 0%, var(--accent-2, #F26B5E) 100%)', opacity:0.25, zIndex:0}}></div>
            {[
              {step:'J0', label:'Inscription', desc:'Email + mot de passe. Zéro CB demandée. Accès immédiat à toutes les fonctionnalités Pro.', color:'var(--primary)'},
              {step:'J0 → J14', label:'Vous testez', desc:'14 jours pour découvrir l\'app sans contrainte. Vos données sont déjà sauvegardées.', color:'var(--primary)'},
              {step:'J14', label:'Vous choisissez', desc:'Restez en plan Découverte gratuit (10 factures/mois) ou souscrivez un plan payant. CB demandée uniquement si vous souscrivez.', color:'var(--accent-2, #F26B5E)'},
              {step:'J14 + 14',label:'1er débit (si abonnement)', desc:'Stripe vous offre encore 14 jours d\'essai. 1er débit en F XPF natif uniquement le 28e jour. Annulable à tout moment depuis le portail client.', color:'var(--accent-2, #F26B5E)'},
            ].map((s, i) => (
              <div key={i} style={{position:'relative', zIndex:1, textAlign:'center'}}>
                <div style={{display:'inline-flex', alignItems:'center', justifyContent:'center', width:64, height:44, borderRadius:22, background:'var(--surface, #fff)', border:`2px solid ${s.color}`, color:s.color, marginBottom:12, fontWeight:700, fontSize:11, fontFamily:'var(--font-mono, monospace)', padding:'0 8px'}}>
                  {s.step}
                </div>
                <h4 style={{fontSize:14, fontWeight:600, margin:'0 0 6px'}}>{s.label}</h4>
                <p style={{fontSize:12, color:'var(--ink-muted)', lineHeight:1.5, margin:0}}>{s.desc}</p>
              </div>
            ))}
          </div>
          <div style={{marginTop:28, padding:'12px 16px', background:'var(--surface-2, #f6f3ed)', borderRadius:8, fontSize:12.5, color:'var(--ink-muted)', textAlign:'center', borderLeft:'3px solid var(--ok, #10b981)'}}>
            <strong style={{color:'var(--ok, #10b981)'}}>Total avant tout débit potentiel : 28 jours</strong>
            {" · "}vous pouvez annuler en 1 clic à tout moment, vous gardez l'accès jusqu'à la fin de la période payée.
          </div>
        </div>
      </section>

      <section className="section" style={{paddingTop:0}}>
        <div className="pricing-grid" data-testid="pricing-grid">
          {orderedPlans.map(plan => {
            const isHighlight = plan.id === "pro";
            const isFree = plan.id === "discovery";
            return (
              <div key={plan.id} className={`price-card ${isHighlight?"highlight":""}`} data-testid={`plan-${plan.id}`}>
                {isHighlight && <div className="badge-top">Le plus populaire</div>}
                <h3>{plan.name}</h3>
                <div style={{fontSize:13, color:'var(--ink-muted)', minHeight:20}}>
                  {plan.id==="discovery" && "Pour découvrir"}
                  {plan.id==="solo" && "Indépendant"}
                  {plan.id==="pro" && "TPE 1-3 personnes"}
                  {plan.id==="business" && "PME 3-10 personnes"}
                </div>
                <div className="price">
                  {isFree ? "0" : getPrice(plan).toLocaleString("fr-FR")}<span className="unit"> F{perLabel}</span>
                </div>
                <div className="price-eur">
                  {isFree ? "—" : `≈ ${getEur(plan)} €${perLabel}`}
                </div>
                <ul>
                  {(features[plan.id]||[]).map((f, i) => {
                    if (typeof f === "object") return <li key={i} className="off">{f.text}</li>;
                    return <li key={i}>{f}</li>;
                  })}
                </ul>
                <div className="cta">
                  {user ? (
                    <Link to="/app/billing" className={`btn ${isHighlight?"btn-primary":"btn-ghost"} btn-full`} data-testid={`cta-${plan.id}`}>
                      {isFree ? "Rester sur ce plan" : "Passer à " + plan.name}
                    </Link>
                  ) : (
                    <Link to="/signup" className={`btn ${isHighlight?"btn-primary":"btn-ghost"} btn-full`} data-testid={`cta-${plan.id}`}>
                      {isFree ? "Commencer gratuitement" : "Essayer 14 jours"}
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="section" style={{maxWidth:900}}>
        <h2 style={{textAlign:'center', marginBottom:40}}>Questions fréquentes</h2>
        <div style={{display:'flex', flexDirection:'column', gap:16}}>
          {[
            ["L'essai gratuit est-il vraiment gratuit ?","Oui, 14 jours avec toutes les fonctionnalités Pro débloquées, sans carte bancaire requise. À la fin des 14 jours, votre compte bascule sur le plan Découverte gratuit (limité à 10 factures/mois). Vous pouvez choisir un plan payant à tout moment pour conserver l'accès Pro."],
            ["Comment fonctionne le passage à un plan payant ?","Au moment de souscrire, Stripe vous demande votre carte. Vous bénéficiez encore de 14 jours d'essai (cette fois avec votre carte enregistrée comme garantie) avant le premier débit. Annulation possible à tout moment via le portail client."],
            ["Puis-je annuler à tout moment ?","Oui, votre abonnement est sans engagement. Annulation en 1 clic depuis le portail Stripe. Vous conservez l'accès jusqu'à la fin de la période payée."],
            ["Que se passe-t-il à la fin de l'essai sans abonnement ?","Aucun débit, votre compte passe simplement en plan Découverte gratuit (10 factures/mois max). Toutes vos données sont conservées."],
            ["Mes données sont-elles en sécurité ?","Oui, elles sont hébergées de manière sécurisée, sauvegardées automatiquement et conformes à la réglementation."],
            ["Dois-je installer quelque chose ?","Non, Gestion Lagon est 100% web. Accessible depuis n'importe quel appareil avec un navigateur. Une version installable (PWA) est également disponible."],
            ["Quelle devise ?","Tarifs affichés en francs pacifiques (XPF). Le paiement Stripe est effectué en euros à l'équivalence (1 000 XPF ≈ 8,38 €)."],
          ].map(([q,a],i) => (
            <div key={i} style={{padding:'20px 24px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)'}}>
              <div style={{fontWeight:600, marginBottom:6, fontFamily:'var(--font-display)', fontSize:17}}>{q}</div>
              <div style={{color:'var(--ink-muted)', fontSize:14, lineHeight:1.6}}>{a}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
